/**
 * AcceptanceBySitePanel — planned-vs-actual delivery rate per pickup site.
 *
 * Phase 16 / Task 16.6 (DEC-163). Table of top-10 sites with a compact
 * progress-bar rate column and the anonymized hauler letter series column.
 */
import React from 'react';
import type { BiDateRange } from '@lodex-pro/shared/validation/disposal-bi-panels';
import { useDisposalBiAcceptanceBySite } from '../../hooks/useDisposalBiPanels';
import { PanelChrome } from './PanelChrome';
import { DateRangeToggle } from './DateRangeToggle';
import { StatCard } from './StatCard';
import { formatNumber } from '@lodex-pro/shared/number-format';

interface Props {
  readonly onModuleInactive?: () => void;
}

export function AcceptanceBySitePanel({ onModuleInactive }: Props): React.JSX.Element | null {
  const [range, setRange] = React.useState<BiDateRange>('30D');
  const { data, isLoading, isError, error } = useDisposalBiAcceptanceBySite(range);

  React.useEffect(() => {
    if (isError && error !== null && error.message.includes('409')) {
      onModuleInactive?.();
    }
  }, [isError, error, onModuleInactive]);

  if (isError && error !== null && error.message.includes('409')) return null;

  const sites = data?.sites ?? [];
  const displayedSites = sites.slice(0, 10);

  const overallRate =
    sites.length === 0
      ? 0
      : sites.reduce((s, x) => s + x.actualCount, 0) /
        Math.max(
          sites.reduce((s, x) => s + x.plannedCount, 0),
          1,
        );
  const belowThreshold = sites.filter((s) => s.rate < 0.8).length;

  return (
    <PanelChrome
      title="Acceptance by Site"
      description="Planned vs actual deliveries per hauler pickup site."
      actions={<DateRangeToggle value={range} onRangeChange={setRange} />}
    >
      <div className="mb-4 grid grid-cols-3 gap-3">
        <StatCard
          label="Overall Rate"
          value={`${formatNumber(overallRate * 100, { decimals: 0 })}%`}
        />
        <StatCard label="# Sites" value={String(sites.length)} />
        <StatCard label="Sites Below 80%" value={String(belowThreshold)} />
      </div>
      {isLoading ? (
        <div className="py-8 text-center text-sm text-[var(--color-text-tertiary)]">Loading…</div>
      ) : displayedSites.length === 0 ? (
        <div className="py-8 text-center text-sm text-[var(--color-text-tertiary)]">
          No deliveries in this range.
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="acceptance-by-site-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[var(--color-text-tertiary)]">
                <th className="py-2 font-medium">Site</th>
                <th className="py-2 font-medium">Series</th>
                <th className="py-2 font-medium">Planned</th>
                <th className="py-2 font-medium">Actual</th>
                <th className="py-2 font-medium">Rate</th>
              </tr>
            </thead>
            <tbody>
              {displayedSites.map((site) => (
                <tr key={`${site.locationId}-${site.seriesKey}`} className="border-b border-border">
                  <td className="py-2 text-foreground">{site.siteName}</td>
                  <td className="py-2 text-[var(--color-text-secondary)]">{site.seriesLabel}</td>
                  <td className="py-2 text-[var(--color-text-secondary)]">
                    {formatNumber(site.plannedCount)}
                  </td>
                  <td className="py-2 text-[var(--color-text-secondary)]">
                    {formatNumber(site.actualCount)}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded bg-secondary">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${Math.min(site.rate * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-[var(--color-text-tertiary)]">
                        {formatNumber(site.rate * 100, { decimals: 0 })}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PanelChrome>
  );
}
